<template>
	<v-card class="ma-5" flat :loading="loading">
		<v-card-subtitle>
			<span class="text-body-1 mt-1">
				<strong>{{ $t("sip-domain") }}:</strong>
				{{ sipDomain || $t("no-sip-domain").toUpperCase() }}
			</span>
		</v-card-subtitle>

		<v-divider />

		<v-card-text class="mt-n2" :style="sipListStyle">
			<v-list>
				<template v-for="item in sipUsers">
					<v-list-item
						:key="item.sipSid"
						class="no-hover"
					>
						<sip-user :user="item" :selected="selected.includes(item.sipSid)" @checkboxChanged="updateSelected" @onEdit="openEditDialog" />
					</v-list-item>
				</template>
			</v-list>
		</v-card-text>

		<v-divider />

		<v-card-actions class="flex-wrap sip-actions">
			<v-spacer />

			<v-btn
				:disabled="disableDeleteUserButton"
				color="primary"
				:class="{ 'text-white': sipDomain?.length && selected?.length }"
				@click="deleteSipUser"
			>
				{{ $t("delete-sip-user") }}
			</v-btn>

			<v-btn :disabled="disableCreateSipDomainButton" color="primary" @click="createSipDomain">
				{{ $t("create-sip-domain") }}
			</v-btn>

			<v-btn :disabled="disableCreateSipUserButton" color="primary" @click="createSipUser">
				{{ $t("create-sip-user") }}
			</v-btn>
		</v-card-actions>

		<confirmation-dialog ref="dialogRef" content-class="devops-subdialog" />
		<create-sip-user-dialog v-model="createSipUserDialog" :groups="allGroupsList" @getUsers="getSipUsers" />
		<edit-sip-user-dialog v-model="editSipUserDialog" :user="editingUser" :groups="allGroupsList" @updated="getSipUsers" />
	</v-card>
</template>

<script>
import SipUser from "@/components/support/sips/SipUser.vue";
import axios from "axios";
import { mapActions, mapGetters } from "vuex";
import ConfirmationDialog from "@/components/configuration/ConfirmationDialog.vue";
import CreateSipUserDialog from "@/components/support/sips/CreateSipUserDialog.vue";
import EditSipUserDialog from "@/components/support/sips/EditSipUserDialog.vue";

export default {
	name: "SipPanel",

	components: {
		CreateSipUserDialog,
		EditSipUserDialog,
		ConfirmationDialog,
		SipUser
	},

	data() {
		return {
			createSipUserDialog: false,
			editSipUserDialog: false,
			editingUser: null,
			fetchedDomainName: undefined,
			allGroupsList: [],
			loading: false,
			selected: [],
			sipUsers: []
		};
	},

	computed: {
		...mapGetters("login", [ "profile"]),

		disableCreateSipDomainButton() {
			return !!this.sipDomain?.length || this.loading;
		},

		disableCreateSipUserButton() {
			return !this.sipDomain?.length || this.loading;
		},

		disableDeleteUserButton() {
			return !this.sipDomain?.length || !this.selected?.length || this.loading;
		},

		sipDomain() {
			return this.sipUsers?.length > 0 ? this.sipUsers[0].domain : this.fetchedDomainName;
		},

		sipListStyle() {
			if (this.$vuetify.breakpoint.xs) {
				return { overflowY: "auto", flex: "1" };
			}
			return { minHeight: "350px", maxHeight: "350px", overflowY: "auto" };
		}
	},

	watch: {
		loading(val) {
			this.$emit("update:loading", val);
		},

		createSipUserDialog(value) {
			if (!value) {
				this.resetSelected();
			}
		}
	},

	async created() {
		await this.getSipUsers();
		await this.getDomain();
	},

	methods: {
		...mapActions("groups", ["getAllGroups"]),

		async createSipDomain() {
			try {
				this.loading = true;
				const { data: domainInstance } = await this.axios.post("voice/v1/sips", { secure: false });

				this.$success(this.$t("create-sip-domain-success"));

				if (domainInstance?.domainName) {
					this.fetchedDomainName = domainInstance.domainName;
				} else {
					await this.getDomain();
				}
			} catch (err) {
				console.error("Error while create SIP Domain:", err);
				this.$error(this.$t("error.create-sip-domain"));
			} finally {
				this.loading = false;
				await this.getSipUsers();
			}
		},

		async createSipUser() {
			this.createSipUserDialog = true;
		},

		openEditDialog(user) {
			this.editingUser = user;
			this.editSipUserDialog = true;
		},

		async deleteSipUser() {
			try {
				const dialogResult = await this.$refs.dialogRef.open({
					title: this.$t("delete-user-title"),
					body: this.$t("delete-user-info"),
					confirm: this.$t("confirm"),
					cancel: this.$t("cancel"),
					showCloseButton: true
				});
			} catch (err) {
				if (err === "cancel") return;
				throw err;
			}

			this.loading = true;
			let countOfFaildRequest = 0;

			await Promise.all(
				this.selected.map(async (sipSid) => {
					try {
						await this.axios.delete(`/sips/v1/user/${sipSid}`);
						this.removeFromSelected(sipSid);
					} catch (err) {
						console.error(`Error while deleting SIP user with sID ${sipSid}:`, err);
						countOfFaildRequest++;
					}
				})
			);

			if (countOfFaildRequest !== 0) {
				this.$error(this.$t("error.delete-sip-users-data"));
			} else {
				this.$success(this.$t("delete-sip-users-data-success"));
			}

			await this.getSipUsers();
			this.loading = false;
		},

		addToSelected(sipSid) {
			this.selected = [...this.selected, sipSid];
		},

		async getDomain() {
			this.loading = true;
			try {
				this.fetchedDomainName = (await this.axios.get("/voice/v1/sips/domain-name")).data;
			} catch (err) {
				console.error("Failed to retrieve the domain name. Data from the SIP user list is being used.");
			} finally {
				this.loading = false;
			}
		},

		async getOrganizationUsers() {
			try {
				return (await this.axios.get(`organizations/${this.profile.clinic}/users`)).data;
			} catch (error) {
				console.error(error);
				this.$error(this.$t("error.get-users"));
			}
		},

		async getSipUsers(options) {
			const silent = options?.silent || false;

			if (!silent) {
				this.loading = true;
				if (this.sipUsers?.length) {
					this.sipUsers = [];
				}
			}

			let newSipUsers = [];

			try {
				newSipUsers = (await this.axios.get("/sips/v1/users/with-passwords")).data;
			} catch (err) {
				console.error(err);
				if (!silent) {
					this.$error(this.$t("error.get-sip-users-data"));
					this.sipUsers = [];
					this.loading = false;
				}
				this.$emit("refreshed");
				return { success: false };
			}

			const userDatas = {};

			if (newSipUsers.length > 0) {
				const organizationUsers = await this.getOrganizationUsers();

				if (organizationUsers) {
					organizationUsers.forEach(user => { userDatas[user.id] = user; });
				}
			}

			if (Object.keys(userDatas).length > 0) {
				const allGroups = await this.getAllGroups();

				this.allGroupsList = allGroups || [];

				newSipUsers.forEach((sipUser) => {
					sipUser.name = userDatas[sipUser.user]?.name || undefined;
					sipUser.groups = allGroups.filter((group) => group.users.includes(sipUser.user));
					sipUser.defaultUserGroupName = allGroups.find((g) => g.id === sipUser.defaultUserGroupId)?.label || null;
				});
			}

			this.sipUsers = [ ...newSipUsers ].sort((a, b) =>
				(a.username || "").localeCompare(b.username || "", undefined, { numeric: true, sensitivity: "base" })
			);
			if (!silent) this.loading = false;
			this.$emit("refreshed");
			return { success: true };
		},

		removeFromSelected(sipSid) {
			this.selected = this.selected.filter(oneSid => oneSid !== sipSid);
		},

		resetSelected() {
			this.selected = [];
		},

		updateSelected(data) {
			const { sipSid, value } = data;

			const existingIndex = this.selected.findIndex(item => item === sipSid);

			if (existingIndex !== -1) {
				if (value === false) {
					this.removeFromSelected(sipSid);
				}
			} else {
				if (value === true) {
					this.addToSelected(sipSid);
				}
			}
		}
	}
};
</script>
	<style scoped>
	.text-white {
		color: white !important;
	}

	@media (max-width: 600px) {
		.sip-actions {
			flex-direction: column;
			align-items: stretch;
			gap: 8px;
		}

		.sip-actions .v-btn {
			width: 100%;
			margin-left: 0 !important;
			margin-right: 0 !important;
		}

		.sip-actions .spacer {
			display: none;
		}
	}

	@media (max-width: 599px) {
		.v-card {
			display: flex;
			flex-direction: column;
			height: 100%;
		}
	}
</style>
